// One <td> of the takeoff grid: display mode + inline editor.
// Editable cells are white, calculated cells grey, values that differ from the
// surface default are blue with a reset icon.
import { memo, useState } from "react";
import { AlertCircle, ArrowLeftRight, Check, RotateCcw } from "lucide-react";
import SearchableSelect from "../../../../../components/ui/SearchableSelect";
import { calcValue, validateCellValue } from "./takeoff-columns";

const optionLabel = (options, value) => options?.find((o) => String(o.value) === String(value ?? ""))?.label ?? (value || "—");

function NumberEditor({ field, seed, initial, onCommit, onCancel }) {
  const [value, setValue] = useState(seed ?? String(initial ?? ""));
  const error = validateCellValue(field, value);
  const commit = (move) => {
    if (error) return;
    onCommit(Number(value), move);
  };
  return (
    <div className="relative">
      <input
        autoFocus
        type="text"
        inputMode="decimal"
        aria-label={field}
        aria-invalid={!!error}
        value={value}
        onFocus={(e) => seed == null && e.target.select()}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => (error ? onCancel() : commit(null))}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit("down"); }
          else if (e.key === "Tab") { e.preventDefault(); commit(e.shiftKey ? "left" : "right"); }
          else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
        }}
        className={`h-[26px] w-full rounded-[3px] border bg-surface px-1.5 text-right text-[12.5px] tabular-nums text-text_primary focus:outline-none ${error ? "border-danger" : "border-border_strong"}`}
      />
      {error && (
        <div role="alert" className="absolute left-0 top-full z-40 mt-1 whitespace-nowrap rounded-[3px] border border-danger_border bg-danger_bg px-2 py-1 text-[11px] font-medium text-danger shadow-sm">
          {error}
        </div>
      )}
    </div>
  );
}

function TextEditor({ seed, initial, onCommit, onCancel }) {
  const [value, setValue] = useState(seed ?? initial ?? "");
  return (
    <input
      autoFocus
      type="text"
      aria-label="Area / Room"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onCommit(value, null)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); onCommit(value, "down"); }
        else if (e.key === "Tab") { e.preventDefault(); onCommit(value, e.shiftKey ? "left" : "right"); }
        else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      }}
      className="h-[26px] w-full rounded-[3px] border border-border_strong bg-surface px-1.5 text-[12.5px] text-text_primary focus:outline-none"
    />
  );
}

// `api` is a stable object from the grid (see TakeoffGrid) so memo() can skip
// cells whose own props did not change.
function TakeoffCell({ col, colIdx, rowIdx, line, result, defaults, options, editing, seed, active, selectedRow, locked, left, api }) {
  const cellId = `tk-${rowIdx}-${colIdx}`;
  const onCommit = (v, move) => api.commit(line.id, col, v, move);
  const onCancel = api.cancel;
  const onToggle = () => api.toggle(line.id, col);
  const onReset = () => api.reset(line.id, col);
  const value = col.field ? line[col.field] : null;
  const fieldError = result?.errors?.[col.field];
  const overridden = col.defaultable && defaults && defaults[col.field] !== undefined && value !== defaults[col.field];
  const isCalc = col.type === "calc" || col.type === "index";

  const base = "h-[34px] border-b border-r border-border_soft px-2 text-[12.5px] whitespace-nowrap overflow-hidden text-ellipsis";
  const bg = selectedRow ? "bg-primary_tint" : isCalc ? "bg-surface_alt" : "bg-surface";
  const sticky = left != null ? "sticky z-10" : "";
  const outline = active ? "outline outline-2 -outline-offset-2 outline-primary" : fieldError ? "outline outline-1 -outline-offset-1 outline-danger" : "";
  const align = col.align === "right" ? "text-right tabular-nums" : "text-left";
  const tone = col.type === "calc" ? "text-text_secondary" : overridden ? "text-info font-medium" : "text-text_primary";

  let content;
  if (editing && !locked) {
    if (col.type === "number") {
      content = <NumberEditor field={col.field} seed={seed} initial={value} onCommit={onCommit} onCancel={onCancel} />;
    } else if (col.type === "text") {
      content = <TextEditor seed={seed} initial={value} onCommit={onCommit} onCancel={onCancel} />;
    } else if (col.type === "select") {
      content = (
        <div className="min-w-[180px]">
          <SearchableSelect
            compact
            autoFocus
            defaultMenuIsOpen
            ariaLabel={col.label}
            options={options}
            value={value ?? ""}
            onChange={(v) => onCommit(v, null)}
            onMenuClose={onCancel}
          />
        </div>
      );
    }
  } else if (col.type === "index") {
    content = (
      <span className="flex items-center justify-between gap-1 text-[11.5px] text-text_muted tabular-nums">
        {rowIdx + 1}
        {result && !result.valid && <AlertCircle size={12} className="text-danger" aria-label="Line has errors" />}
      </span>
    );
  } else if (col.type === "bool") {
    content = (
      <button
        type="button"
        tabIndex={-1}
        disabled={locked}
        aria-label={`${col.label} ${value ? "yes" : "no"}`}
        aria-pressed={!!value}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className={`mx-auto flex h-4 w-4 items-center justify-center rounded-[3px] border ${value ? "border-primary bg-primary text-white" : "border-border_strong bg-surface"} ${overridden ? "outline outline-1 outline-offset-1 outline-info" : ""} disabled:cursor-not-allowed`}
      >
        {value && <Check size={11} strokeWidth={3} aria-hidden="true" />}
      </button>
    );
  } else if (col.type === "calc") {
    const v = calcValue(col, result);
    content = v == null || (result && !result.valid) ? <span className="text-text_muted">—</span> : col.format(v);
  } else if (col.type === "select") {
    const label = optionLabel(options, value);
    const swapped = col.field === "finishProductId" ? result?.finishProductId : col.field === "primerProductId" ? result?.primerProduct?.id : null;
    content = (
      <span className="flex items-center gap-1" title={fieldError || label}>
        <span className="truncate">{fieldError && !value ? "Pick a surface" : label}</span>
        {swapped && value && swapped !== value && (
          <span className="flex flex-shrink-0 items-center gap-0.5 text-[10.5px] text-text_muted" title={`Brand Mode uses ${swapped}`}>
            <ArrowLeftRight size={10} aria-hidden="true" />
            {swapped}
          </span>
        )}
      </span>
    );
  } else {
    content = <span title={fieldError || undefined}>{col.format && value !== "" && value != null ? col.format(value) : value}</span>;
  }

  return (
    <td
      id={cellId}
      role="gridcell"
      aria-readonly={isCalc || locked}
      aria-invalid={!!fieldError}
      aria-selected={active}
      style={left != null ? { left } : undefined}
      className={`${base} ${bg} ${sticky} ${outline} ${align} ${tone} ${editing ? "overflow-visible" : ""} relative`}
      onMouseDown={(e) => api.cellMouseDown(rowIdx, colIdx, e)}
    >
      <div className="flex items-center gap-1">
        <div className="min-w-0 flex-1">{content}</div>
        {overridden && !editing && !locked && col.type !== "bool" && (
          <button
            type="button"
            tabIndex={-1}
            aria-label={`Reset ${col.label} to default`}
            title={`Reset to default (${optionLabel(options, defaults[col.field])})`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              onReset();
            }}
            className="flex-shrink-0 text-info"
          >
            <RotateCcw size={11} aria-hidden="true" />
          </button>
        )}
      </div>
    </td>
  );
}

export default memo(TakeoffCell);
